import {
  Card,
  CircularProgress,
  circularProgressClasses,
  Collapse,
  Fade,
  Grid,
  Icon,
  Modal,
  Tooltip,
} from "@mui/material";
import ModalImage from "components/Modals/ModalImage";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useState } from "react";
import { API_URL } from "services/config";
import { getColor } from "utils/colors";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "50%", xxl: "50%" },
  overflowY: "auto",
};

export default function ModalComparadorArchivos({
  open,
  handleClose,
  archivoOriginal,
  archivosSimilares,
}) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [expanded, setExpanded] = useState(null);
  
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" p={3} pb={0}>
              <SoftBox>
                <SoftTypography variant="h6">Comparar archivos similares</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                <SoftBox display="flex" alignItems="center">
                  <Tooltip title="Cerrar" placement="top">
                    <Icon
                      fontSize="medium"
                      onClick={() => {
                        handleClose();
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox p={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <SoftBox p={2}>
                      <SoftBox>
                        <SoftTypography variant="h6">Archivo original</SoftTypography>
                      </SoftBox>
                      {archivoOriginal?.file?.includes("pdf") ? (
                        <SoftBox mt={2}>
                          <SoftButton
                            color="primary"
                            fullWidth
                            onClick={() => {
                              window.open(`${API_URL}/${archivoOriginal?.file}`, "_blank");
                            }}
                          >
                            Abrir pdf &nbsp;
                            <Icon>arrow_forward</Icon>
                          </SoftButton>
                        </SoftBox>
                      ) : (
                        <SoftBox
                          mt={2}
                          component="img"
                          src={`${API_URL}/${archivoOriginal?.file}`}
                          alt="Archivo original"
                          borderRadius="lg"
                          sx={{
                            width: "100%",
                            overflowY: "auto",
                            "&:hover": {
                              cursor: "pointer",
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => setSelectedImage(`${API_URL}/${archivoOriginal?.file}`)}
                        />
                      )}
                    </SoftBox>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <SoftBox p={2}>
                      <SoftTypography variant="h6">Archivos similares</SoftTypography>
                    </SoftBox>
                    <Grid container spacing={2} p={2} pt={0}>
                      {archivosSimilares
                        .sort((a, b) => b.similitud - a.similitud)
                        .map((archivo, index) => (
                          <Grid item xs={12} key={index}>
                            <Card
                              sx={{
                                border: "1px solid rgba(0, 0, 0, 0.12)",
                              }}
                            >
                              <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                px={2}
                                py={1}
                                sx={{
                                  borderBottom:
                                    expanded !== archivo.file.id
                                      ? "none"
                                      : "1px solid rgba(0, 0, 0, 0.12)",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                  },
                                }}
                                onClick={() =>
                                  setExpanded(expanded === archivo.file.id ? null : archivo.file.id)
                                }
                              >
                                <SoftBox display="flex" alignItems="center">
                                  <SoftBox mr={1} display="flex" alignItems="center">
                                    <Tooltip title="Porcentaje de similitud" placement="top">
                                      <SoftBox
                                        position="relative"
                                        display="flex"
                                        justifyContent="center"
                                      >
                                        <CircularProgress
                                          variant="determinate"
                                          sx={(theme) => ({
                                            color: theme.palette.grey[200],
                                            ...theme.applyStyles("dark", {
                                              color: theme.palette.grey[800],
                                            }),
                                          })}
                                          size={40}
                                          thickness={4}
                                          value={100}
                                        />
                                        <CircularProgress
                                          variant="determinate"
                                          disableShrink
                                          color={archivo.similitud > 95 ? "error" : "warning"}
                                          sx={() => ({
                                            animationDuration: "550ms",
                                            position: "absolute",
                                            [`& .${circularProgressClasses.circle}`]: {
                                              strokeLinecap: "round",
                                            },
                                          })}
                                          value={archivo.similitud}
                                          size={40}
                                          thickness={4}
                                        />
                                        <SoftBox
                                          sx={{
                                            top: 0,
                                            left: 0,
                                            bottom: 0,
                                            right: 0,
                                            position: "absolute",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <SoftTypography variant="caption">
                                            {`${Math.round(archivo.similitud)}%`}
                                          </SoftTypography>
                                        </SoftBox>
                                      </SoftBox>
                                    </Tooltip>
                                  </SoftBox>
                                  <SoftBox display="flex" flexDirection="column">
                                    <SoftTypography variant="h6" fontWeight="bold">
                                      {`Permiso Nº${archivo?.file?.permiso?.anioPermiso} - ${archivo?.file?.permiso?.nroPermiso}`}
                                    </SoftTypography>
                                    <SoftTypography variant="caption" fontWeight="regular">
                                      {`Cargado por ${archivo?.file?.usuario?.nombre} ${
                                        archivo?.file?.usuario?.apellido
                                      } el ${dayjs(archivo?.file?.createdAt).format("DD/MM/YYYY")}`}
                                    </SoftTypography>
                                  </SoftBox>
                                </SoftBox>
                                <SoftBox
                                  display="flex"
                                  justifyContent="flex-end"
                                  alignItems="center"
                                >
                                  <SoftBox>
                                    <Tooltip
                                      title={expanded === archivo.file.id ? "Ocultar" : "Mostrar"}
                                      placement="top"
                                    >
                                      <SoftButton
                                        color="primary"
                                        iconOnly
                                        onClick={() =>
                                          setExpanded(
                                            expanded === archivo.file.id ? null : archivo.file.id
                                          )
                                        }
                                      >
                                        <Icon
                                          sx={{
                                            rotate:
                                              expanded === archivo.file.id ? "180deg" : "0deg",
                                            transition: "all 0.5s",
                                          }}
                                        >
                                          expand_more
                                        </Icon>
                                      </SoftButton>
                                    </Tooltip>
                                  </SoftBox>
                                </SoftBox>
                              </SoftBox>
                              <Collapse in={expanded === archivo.file.id}>
                                {archivo?.file?.file?.includes(".pdf") ? (
                                  <SoftBox p={2}>
                                    <SoftButton
                                      color="primary"
                                      fullWidth
                                      onClick={() => {
                                        window.open(`${API_URL}/${archivo?.file?.file}`, "_blank");
                                      }}
                                    >
                                      Abrir pdf &nbsp;
                                      <Icon>arrow_forward</Icon>
                                    </SoftButton>
                                  </SoftBox>
                                ) : (
                                  <SoftBox
                                    component="img"
                                    src={`${API_URL}/${archivo?.file?.file}`}
                                    alt="Archivo original"
                                    sx={{
                                      width: "100%",
                                      overflowY: "auto",
                                      "&:hover": {
                                        cursor: "pointer",
                                        opacity: 0.8,
                                      },
                                    }}
                                    onClick={() =>
                                      setSelectedImage(`${API_URL}/${archivo?.file?.file}`)
                                    }
                                  />
                                )}
                              </Collapse>
                            </Card>
                          </Grid>
                        ))}
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </SoftBox>
            <ModalImage
              open={selectedImage !== null}
              handleClose={() => setSelectedImage(null)}
              src={selectedImage}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalComparadorArchivos.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  archivoOriginal: PropTypes.object,
  archivosSimilares: PropTypes.array,
};
